import EditOffice from '@/features/admin/components/administration/EditOffice';
import { createFileRoute, Navigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useCanManageOffice from '@/features/user/hooks/useCanManageOffice';
import { AdminContentSkeleton } from '@/shared/components/feedback/skeletons';
import { ErrorState } from '@/shared/components/feedback';
import { AdminHeader, AdminPageLayout } from '@/shared/components/ui/AdminHeader';
import { Monitor } from 'lucide-react';

function OfficePageSkeleton() {
  return (
    <AdminPageLayout
      header={
        <AdminHeader
          icon={Monitor}
          title="Workspace Management"
          description="Configure and manage all desks and meeting rooms"
          variant="indigo"
        />
      }
    >
      <AdminContentSkeleton />
    </AdminPageLayout>
  );
}

export const Route = createFileRoute('/admin/offices/$officeId')({
  component: RouteComponent,
  pendingComponent: OfficePageSkeleton,
});

function RouteComponent() {
  const { officeId } = Route.useParams();
  const { user, loading: authLoading, roleReady } = useAuth();
  const {
    canManage,
    isLoading: canManageLoading,
    error: canManageError,
  } = useCanManageOffice(user?.uid, officeId);
  const location = useLocation();

  if (authLoading || !roleReady || !officeId) return null;

  if (!user) {
    return <Navigate to="/login" search={{ from: location.pathname }} replace />;
  }

  if (user.role === 'user') {
    return <Navigate to="/" replace />;
  }

  // Admins can open any office. Managers wait for the per-office check, and
  // leave before the workspace page renders if this one isn't theirs.
  if (user.role !== 'admin') {
    if (canManageLoading) return null;

    if (canManageError) {
      return (
        <AdminPageLayout
          header={
            <AdminHeader
              icon={Monitor}
              title="Workspace Management"
              description="Configure and manage all desks and meeting rooms"
              variant="indigo"
            />
          }
        >
          <ErrorState
            title="Failed to load permissions"
            description="We couldn't verify your permissions for this office. Please try again."
          />
        </AdminPageLayout>
      );
    }

    if (!canManage) {
      return <Navigate to="/admin/offices" replace />;
    }
  }

  return <EditOffice officeID={officeId} />;
}
